/**
 * Home's feed (D354): the cards in one row, the middle one large with its neighbours peeking,
 * a counter, arrows and the keyboard. The row is HeroUI Pro's `Carousel` (Embla underneath) used
 * as the engine: the arrows are ours, in the gutters outside the track (the component's own sat
 * at the end of the whole track, off screen, or under the peeking neighbour), and so is the
 * drag, because the HoloCard under the pointer takes the press: a pointer-down plus a move of
 * six pixels or more drags the row (the tilt stands still meanwhile), a short flick moves a
 * card, the wheel moves it sideways, and the arrow keys still work.
 *
 * Every card is reachable by Tab and announced with its place ("3 of 9"); Enter opens the
 * centre card, `+` `-` `p` `h` `r` are its actions, and the arrows move.
 *
 * Ported from the workbench's components/home/HomeFeed.tsx: the empty feed's one action is the caller's (the
 * desktop starts a chat; the console opens the Inbox).
 */
import { Bell, ChevronLeft, ChevronRight } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { Carousel } from '@heroui-pro/react/carousel';
import type { EmblaCarouselType } from 'embla-carousel';
import { type KeyboardEvent, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react';

import type { FeedPrefs } from '../../sharedHome.js';
import { type FeedItem, positionWords } from '../../sharedHome.js';
import { ArtEmpty } from '../ui/Art.js';
import { Hint } from '../ui/Hint.js';
import { FeedCard } from './FeedCard.js';
import { buttonStates, type CardPress, DRAG_START_PX, dragOutcome, hiddenWords, pressWords, UNDO_MS } from './feedFeedbackModel.js';
import { hideFeedItem, pinFeedItem, rateFeedKind, showFeedItem } from './feedPrefs.js';

/** How long a hidden card takes to fold away before the next slides in. */
const HIDE_MS = 220;
/** How long a card keeps what a press said. */
const SAY_MS = 2200;

export interface HomeFeedProps {
  items: readonly FeedItem[];
  prefs: FeedPrefs;
  /** Why the feed is empty, in words. */
  emptyTitle: string;
  emptyText: string;
  onOpen: (item: FeedItem) => void;
  onOpenView: (view: string) => void;
  onRefresh: () => void;
  /** Reports the counter text to the head above the row. */
  onPosition: (index: number, total: number) => void;
  /** The pressed day, so a change starts the row from its first card. */
  dayKey: number;
  /** The empty feed's one action: its words, and what it does. */
  emptyAction: { label: string; onPress: () => void };
}

export function HomeFeed({ items, prefs, emptyTitle, emptyText, onOpen, onOpenView, onRefresh, onPosition, dayKey, emptyAction }: HomeFeedProps) {
  const [api, setApi] = useState<EmblaCarouselType>();
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [said, setSaid] = useState<{ id: string; text: string } | null>(null);
  const [hidden, setHidden] = useState<{ id: string; title: string } | null>(null);
  const [pressing, setPressing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; id: number; at: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const follow = useRef<string | null>(null);
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);
  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      setIndex(api.selectedScrollSnap());
    };
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    api.on('resize', onSelect);
    api.on('slidesChanged', onSelect);
    // The first measure can come before the row has its width.
    const settle = window.setTimeout(onSelect, 150);
    return () => {
      window.clearTimeout(settle);
      api.off('select', onSelect);
      api.off('reInit', onSelect);
      api.off('resize', onSelect);
      api.off('slidesChanged', onSelect);
    };
  }, [api]);

  // A new day, or a feed that got shorter, starts from the first card rather than a slot that is gone.
  useEffect(() => {
    api?.scrollTo(0, true);
  }, [api, dayKey]);
  useEffect(() => {
    api?.reInit();
  }, [api, items.length]);

  // A card that was pinned or unpinned moves in the order: the row follows it.
  useEffect(() => {
    if (!follow.current || !api) return;
    const at = items.findIndex((x) => x.id === follow.current);
    follow.current = null;
    if (at >= 0) api.scrollTo(at);
  }, [items, api]);

  const current = Math.min(index, Math.max(0, items.length - 1));
  // Every card is a stop, so the ends are the first and the last card.
  const edges = { prev: current > 0, next: current < items.length - 1 };
  useEffect(() => {
    onPosition(current, items.length);
  }, [current, items.length, onPosition]);

  const say = useCallback(
    (id: string, press: CardPress, wasOn = false) => {
      const text = pressWords(press, wasOn);
      setSaid({ id, text });
      // Only its own line is taken down: a later press has put another there.
      later(() => setSaid((s) => (s?.id === id && s.text === text ? null : s)), SAY_MS);
    },
    [later],
  );

  const hide = useCallback(
    (item: FeedItem) => {
      setLeaving(item.id);
      later(() => {
        hideFeedItem(item);
        setLeaving(null);
        setHidden({ id: item.id, title: item.title });
        later(() => setHidden((h) => (h?.id === item.id ? null : h)), UNDO_MS);
      }, HIDE_MS);
    },
    [later],
  );

  const refresh = useCallback(
    (item: FeedItem) => {
      setRefreshing(item.id);
      say(item.id, 'refreshing');
      onRefresh();
      later(() => {
        setRefreshing(null);
        say(item.id, 'refreshed');
      }, 700);
    },
    [onRefresh, say, later],
  );

  const rate = useCallback(
    (item: FeedItem, verdict: 'useful' | 'not-useful') => {
      const was = verdict === 'useful' ? buttonStates(prefs, item, false).useful : buttonStates(prefs, item, false).notUseful;
      rateFeedKind(item.kind, verdict);
      say(item.id, verdict, was);
    },
    [prefs, say],
  );

  const pin = useCallback(
    (item: FeedItem) => {
      const on = prefs.pinned.includes(item.id);
      follow.current = item.id;
      pinFeedItem(item.id, !on);
      say(item.id, on ? 'unpin' : 'pin');
    },
    [prefs.pinned, say],
  );

  // ── Drag: the press that the HoloCard would otherwise keep ─────────────
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [data-no-drag]')) return;
    drag.current = { x: e.clientX, id: e.pointerId, at: performance.now(), moved: false };
    setPressing(true);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    if (!d.moved) {
      if (Math.abs(dx) < DRAG_START_PX) return;
      d.moved = true;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* a synthetic pointer has nothing to capture; the drag still follows the moves it gets */
      }
      setDragging(true);
    }
    stage.current?.style.setProperty('--drag-x', `${dx}px`);
  };
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    setPressing(false);
    if (d.moved) {
      const dx = e.clientX - d.x;
      suppressClick.current = true;
      later(() => (suppressClick.current = false), 60);
      const outcome = cancelled ? 'stay' : dragOutcome(dx, performance.now() - d.at);
      if (outcome === 'next') api?.scrollNext();
      else if (outcome === 'previous') api?.scrollPrev();
    }
    stage.current?.style.removeProperty('--drag-x');
    setDragging(false);
  };

  // The wheel moves the row sideways; at either end it lets the page have the wheel back.
  useEffect(() => {
    const node = stage.current;
    if (!node || !api) return;
    let acc = 0;
    let last = 0;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return;
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!delta) return;
      const forward = delta > 0;
      if (forward ? !api.canScrollNext() : !api.canScrollPrev()) return;
      e.preventDefault();
      acc += delta;
      const now = performance.now();
      if (Math.abs(acc) < 40 || now - last < 280) return;
      last = now;
      if (acc > 0) api.scrollNext();
      else api.scrollPrev();
      acc = 0;
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [api]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // A key typed into a field, or a press on a button's own Enter/Space, is not ours.
    if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const item = items[current];
    switch (e.key) {
      case 'ArrowLeft':
        api?.scrollPrev();
        break;
      case 'ArrowRight':
        api?.scrollNext();
        break;
      case 'Enter':
        if (target.closest('button')) return;
        if (item) onOpen(item);
        break;
      case '+':
        if (item) rate(item, 'useful');
        break;
      case '-':
        if (item) rate(item, 'not-useful');
        break;
      case 'p':
        if (item) pin(item);
        break;
      case 'h':
        if (item) hide(item);
        break;
      case 'r':
        if (item) refresh(item);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  if (!items.length) {
    return (
      <div className="home-feed home-feed--empty" data-home-feed-empty>
        <ArtEmpty
          tone="teal"
          icon={<Bell />}
          title={emptyTitle}
          action={
            <Button size="sm" variant="secondary" onPress={emptyAction.onPress}>
              {emptyAction.label}
            </Button>
          }
        >
          {emptyText}
        </ArtEmpty>
        {hidden ? <HiddenLine hidden={hidden} onUndo={() => { showFeedItem(hidden.id); setHidden(null); }} /> : null}
      </div>
    );
  }

  return (
    <div className="home-feed" role="region" aria-roledescription="carousel" aria-label="Feed" onKeyDown={onKeyDown} data-home-feed data-api={api ? "1" : "0"}>
      <div className="home-feed__row">
        <Hint text="Previous card">
          <Button isIconOnly variant="secondary" size="md" className="home-feed__arrow" aria-label="Previous card" isDisabled={!edges.prev} onPress={() => api?.scrollPrev()} data-feed-prev>
            <ChevronLeft className="size-5" />
          </Button>
        </Hint>
        <div
          ref={stage}
          className="home-feed__stage"
          data-dragging={dragging || undefined}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => endDrag(e, false)}
          onPointerCancel={(e) => endDrag(e, true)}
          onClickCapture={(e) => {
            if (!suppressClick.current) return;
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Carousel opts={{ align: 'center', loop: false, duration: 24, watchDrag: false }} setApi={setApi} className="home-feed__carousel">
            <Carousel.Content>
              {items.map((item, i) => {
                const offset = i - current;
                const slot = Math.max(-2, Math.min(2, offset));
                return (
                  <Carousel.Item
                    key={item.id}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${item.kicker}, ${positionWords(i, items.length)}`}
                    className="home-feed__item"
                    data-slot={slot}
                    data-leaving={leaving === item.id || undefined}
                  >
                    <FeedCard
                      item={item}
                      centre={offset === 0}
                      pressing={pressing}
                      states={buttonStates(prefs, item, refreshing === item.id)}
                      {...(said && said.id === item.id ? { said: said.text } : {})}
                      onUseful={() => rate(item, 'useful')}
                      onNotUseful={() => rate(item, 'not-useful')}
                      onPin={() => pin(item)}
                      onHide={() => hide(item)}
                      onRefresh={() => refresh(item)}
                      onOpen={() => onOpen(item)}
                      onOpenView={onOpenView}
                      onAnswered={onRefresh}
                    />
                  </Carousel.Item>
                );
              })}
            </Carousel.Content>
          </Carousel>
        </div>
        <Hint text="Next card">
          <Button isIconOnly variant="secondary" size="md" className="home-feed__arrow" aria-label="Next card" isDisabled={!edges.next} onPress={() => api?.scrollNext()} data-feed-next>
            <ChevronRight className="size-5" />
          </Button>
        </Hint>
      </div>
      {hidden ? <HiddenLine hidden={hidden} onUndo={() => { showFeedItem(hidden.id); setHidden(null); }} /> : null}
      <p className="sr-only" aria-live="polite">
        {said ? said.text : positionWords(current, items.length)}
      </p>
    </div>
  );
}

/** The way back from a hide, in place under the row. */
function HiddenLine({ hidden, onUndo }: { hidden: { id: string; title: string }; onUndo: () => void }) {
  return (
    <p className="home-feed__hidden" role="status" data-feed-hidden>
      <span>{hiddenWords(hidden.title)}</span>
      <Button size="sm" variant="ghost" onPress={onUndo} data-feed-undo>
        Undo
      </Button>
    </p>
  );
}
